import React, { useState, useEffect } from 'react';
import { Modal, FormInput, FormSelect, SearchableSelect } from '@/components/ui';
import { fmt } from '@/lib/formatters';
import { Wallet, CheckCircle2, CreditCard, Landmark, Banknote, Search } from 'lucide-react';
import toast from 'react-hot-toast';

export const CreditPaymentForm = ({ isOpen, onClose, onConfirm, projects = [], defaultStage = 'milestone', readOnlyStage = false }) => {
    const [form, setForm] = useState({
        relatedProjectId: '',
        relatedProjectCollection: '',
        stage: defaultStage,
        paymentDate: new Date().toISOString().split('T')[0],
        amount: '',
        method: 'bank_transfer',
        transactionId: '',
        bankName: '',
        branch: '',
        ifscCode: '',
        upiPhone: '',
        receiverName: '',
        receiverPhone: '',
        chequeNumber: '',
        chequeDate: '',
        remarks: ''
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setForm({
                relatedProjectId: '',
                relatedProjectCollection: '',
                stage: defaultStage,
                paymentDate: new Date().toISOString().split('T')[0],
                amount: '',
                method: 'bank_transfer',
                transactionId: '',
                bankName: '',
                branch: '',
                ifscCode: '',
                upiPhone: '',
                receiverName: '',
                receiverPhone: '',
                chequeNumber: '',
                chequeDate: '',
                remarks: ''
            });
            
            if (projects.length === 1) {
                handleProjectSelect(projects[0]);
            }
        }
    }, [isOpen, projects]);

    const handleProjectSelect = (p) => {
        setForm(prev => ({
            ...prev,
            relatedProjectId: p._id || p.projectId,
            relatedProjectCollection: p.model || 'VerveContact'
        }));
    };

    const handleSubmit = async () => {
        const payAmount = Number(form.amount);
        if (!form.relatedProjectId) {
            return toast.error("Please select a Project");
        }
        if (!payAmount || payAmount <= 0) {
            return toast.error("Please enter a valid payment amount");
        }
        if (!form.stage) {
            return toast.error("Please select a payment type");
        }
        if (!form.paymentDate) {
            return toast.error("Please enter a transaction date");
        }
        /* Developer : Nithin - Date : 21-07-2026 - Added 10-digit phone number and mandatory Receiver Name validations for UPI and Cash */
        if (form.method === 'upi_qr' && (!form.upiPhone || !/^\d{10}$/.test(form.upiPhone.trim()))) return toast.error("Please enter a valid 10-digit UPI Phone Number");
        if (form.method === 'cash' && (!form.receiverName || !form.receiverName.trim())) return toast.error("Please enter Receiver Name");
        if (form.method === 'cash' && (!form.receiverPhone || !/^\d{10}$/.test(form.receiverPhone.trim()))) return toast.error("Please enter a valid 10-digit Receiver Phone Number");

        setLoading(true);
        try {
            if (onConfirm) {
                await onConfirm(form);
            } else {
                toast.success("Received amount recorded successfully!");
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to record payment");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex flex-col gap-0.5">
                    <span className="text-[14px] font-bold text-white font-serif tracking-widest uppercase">
                        Verve Kitchens
                    </span>
                    <span className="text-[9px] text-slate-300 font-semibold tracking-widest uppercase">
                        Add Received Amount
                    </span>
                </div>
            }
            size="max-w-md"
        >
            <div className="space-y-3">
                {/* Header Info */}
                <div className="p-2 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-brand-blue flex-shrink-0">
                        <Wallet size={20} />
                    </div>
                    <div>
                        <h4 className="text-[11px] font-bold text-blue-900 tracking-wider uppercase">Received Amount Entry</h4>
                        <p className="text-[10px] font-medium text-brand-blue uppercase">Record Client Payment</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3 items-start">
                        {projects.length === 1 ? (
                            <div>
                                <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Selected Project *</label>
                                <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium">
                                    {projects[0].customerName || projects[0].firstName || "Unknown Customer"} — {projects[0].customerCustomId || projects[0].projectId || projects[0].customId || '-'}
                                </div>
                            </div>
                        ) : (
                            <SearchableSelect
                                label="Select Project *"
                                placeholder="Search Project / ID..."
                                value={form.relatedProjectId}
                                options={projects.map(p => ({
                                    label: p.customerName || p.firstName || "Unknown Customer",
                                    dropdownLabel: `${p.customerName || p.firstName} — ${p.customerCustomId || p.projectId || p.customId}`,
                                    value: p._id || p.projectId,
                                    model: p.model || 'VerveContact'
                                }))}
                                onChange={(val) => {
                                    const selected = projects.find(p => (p._id || p.projectId) === val);
                                    if (selected) {
                                        setForm(prev => ({
                                            ...prev,
                                            relatedProjectId: val,
                                            relatedProjectCollection: selected.model || 'VerveContact'
                                        }));
                                    }
                                }}
                            />
                        )}
                        <FormInput
                            label="Transaction Date *"
                            type="date"
                            value={form.paymentDate}
                            onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <FormInput
                            label="Amount Received (₹) *"
                            type="number"
                            placeholder="0.00"
                            value={form.amount}
                            inputClassName="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        />
                        {readOnlyStage ? (
                            <div>
                                <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Payment type *</label>
                                <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium capitalize">
                                    {form.stage}
                                </div>
                            </div>
                        ) : (
                            <FormSelect
                                label="Payment type *"
                                value={form.stage}
                                onChange={(e) => setForm({ ...form, stage: e.target.value })}
                                options={[
                                    { value: "milestone", label: "Milestone payment" },
                                    { value: "advance", label: "Advance" },
                                    { value: "ordering", label: "Ordering" },
                                    { value: "balance", label: "Balance" },
                                    { value: "other", label: "Other" }
                                ]}
                            />
                        )}
                    </div>

                    <div>
                        <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Payment Mode</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                { id: 'bank_transfer', label: 'Bank', icon: <Landmark size={14} /> },
                                { id: 'upi_qr', label: 'UPI', icon: <CreditCard size={14} /> },
                                { id: 'cash', label: 'Cash', icon: <Banknote size={14} /> },
                                { id: 'cheque', label: 'Cheque', icon: <CheckCircle2 size={14} /> }
                            ].map(m => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setForm({ ...form, method: m.id })}
                                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl border-2 transition-all ${
                                        form.method === m.id 
                                        ? 'bg-brand-blue/5 border-brand-blue text-brand-blue shadow-sm' 
                                        : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                    }`}
                                >
                                    {m.icon}
                                    <span className="text-[10px] font-bold uppercase tracking-wider">{m.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Dynamic Fields */}
                    {form.method === 'bank_transfer' && (
                        <div className="grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="Transaction / Ref ID"
                                placeholder="TRX-123..."
                                value={form.transactionId}
                                onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                            />
                            <FormInput 
                                label="Bank Name"
                                placeholder="HDFC, SBI..."
                                value={form.bankName}
                                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                            />
                            <FormInput 
                                label="Branch"
                                placeholder="Branch Name"
                                value={form.branch}
                                onChange={(e) => setForm({ ...form, branch: e.target.value })}
                            />
                            <FormInput 
                                label="IFSC Code"
                                placeholder="SBIN0001234"
                                value={form.ifscCode}
                                onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
                            />
                        </div>
                    )}

                    {form.method === 'upi_qr' && (
                        <div className="grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="UPI UTR / Ref No."
                                placeholder="TRX-123..."
                                value={form.transactionId}
                                onChange={(e) => setForm({ ...form, transactionId: e.target.value })}
                            />
                            <FormInput 
                                label="UPI Phone Number"
                                required={true}
                                maxLength={10}
                                placeholder="10-digit number"
                                value={form.upiPhone}
                                onChange={(e) => setForm({ ...form, upiPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            />
                        </div>
                    )}

                    {form.method === 'cash' && (
                        <div className="grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="Receiver Name"
                                required={true}
                                placeholder="Who received"
                                value={form.receiverName}
                                onChange={(e) => setForm({ ...form, receiverName: e.target.value })}
                            />
                            <FormInput 
                                label="Receiver Phone"
                                required={true}
                                maxLength={10}
                                placeholder="10-digit number"
                                value={form.receiverPhone}
                                onChange={(e) => setForm({ ...form, receiverPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            />
                        </div>
                    )}

                    {form.method === 'cheque' && (
                        <div className="grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                            <FormInput 
                                label="Cheque Number"
                                placeholder="6-digit No."
                                value={form.chequeNumber}
                                onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })}
                            />
                            <FormInput 
                                label="Cheque Date"
                                type="date"
                                value={form.chequeDate}
                                onChange={(e) => setForm({ ...form, chequeDate: e.target.value })}
                            />
                            <FormInput 
                                label="Issuing Bank"
                                placeholder="Bank name"
                                value={form.bankName}
                                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                            />
                        </div>
                    )}

                    <div>
                        <label className="text-slate-700 font-bold text-[12px] tracking-wide mb-1 block">Internal Remarks</label>
                        <textarea
                            rows={2}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all resize-none"
                            placeholder="Optional notes..."
                            value={form.remarks}
                            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                        />
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex gap-3 pt-2">
                    <button
                        className="flex-1 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Discard
                    </button>
                    <button
                        className="flex-1 py-2.5 bg-slate-900 hover:bg-black text-white rounded-md text-xs font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                        onClick={handleSubmit}
                        disabled={loading || !form.amount || Number(form.amount) <= 0}
                    >
                        {loading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <CheckCircle2 size={14} />
                                Record Payout
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
